import { formatDateTime } from '@takumi/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, CircleAlert, CircleDashed, Plug, RefreshCcw } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { errorMessage } from '@/lib/api';
import { useInvalidateCrm } from '@/lib/queries';
import { Badge, Card, CardHeader, PageLoader } from '@/components/ui/primitives';
import { api } from '@/lib/api';

export interface IntegrationStatus {
  provider: string;
  name: string;
  status: 'connected' | 'not_configured' | 'error' | 'disabled';
  description: string;
  missing: string[];
  lastSyncAt: string | null;
  lastError: string | null;
  details?: Record<string, string | number | null>;
  canSync?: boolean;
}

interface SyncResult {
  created?: number;
  attached?: number;
  pendingReview?: number;
  duplicates?: number;
  spam?: number;
  fetched?: number;
  leadEmails?: number;
}

/** Real connection state only — anything without credentials shows "נדרש חיבור" and what is missing. */
export function IntegrationsSettings() {
  const { data } = useQuery({ queryKey: ['integrations'], queryFn: () => api.get<IntegrationStatus[]>('/integrations') });
  const qc = useQueryClient();
  const invalidate = useInvalidateCrm();
  const sync = useMutation({
    mutationFn: (provider: string) => api.post<SyncResult>(`/integrations/${provider}/sync`),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ['integrations'] });
      void qc.invalidateQueries({ queryKey: ['submissions'] });
      void qc.invalidateQueries({ queryKey: ['reviews'] });
      invalidate();
      toast.success(`הסנכרון הושלם: ${r.created ?? 0} לידים חדשים, ${r.attached ?? 0} צורפו לקיימים${r.pendingReview ? `, ${r.pendingReview} לבדיקת כפילות` : ''}`);
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  if (!data) return <PageLoader />;
  return (
    <div className="flex flex-col gap-4">
      {data.map((i) => (
        <Card key={i.provider}>
          <CardHeader
            title={i.name}
            icon={<Plug />}
            actions={
              <div className="flex items-center gap-2">
                {i.canSync && (
                  <Button size="sm" variant="secondary" loading={sync.isPending && sync.variables === i.provider} onClick={() => sync.mutate(i.provider)}>
                    <RefreshCcw /> סנכרון עכשיו
                  </Button>
                )}
                {i.status === 'connected' ? (
                <Badge variant="matcha"><CheckCircle2 className="size-3" /> מחובר</Badge>
              ) : i.status === 'error' ? (
                <Badge variant="overdue"><CircleAlert className="size-3" /> שגיאה</Badge>
              ) : (
                <Badge variant="today"><CircleDashed className="size-3" /> נדרש חיבור</Badge>
                )}
              </div>
            }
          />
          <div className="space-y-2 p-4 text-sm">
            <p className="text-ink-soft">{i.description}</p>
            {i.missing.length > 0 && (
              <div className="rounded-control bg-today-bg p-3">
                <div className="font-semibold text-today">חסרה הגדרה:</div>
                <ul className="mt-1 list-disc ps-5">
                  {i.missing.map((m) => <li key={m} className="ltr text-start">{m}</li>)}
                </ul>
              </div>
            )}
            {i.lastSyncAt && <p className="text-muted">סנכרון אחרון: {formatDateTime(i.lastSyncAt)}</p>}
            {i.lastError && <p className="text-overdue">שגיאה אחרונה: {i.lastError}</p>}
            {i.details &&
              Object.entries(i.details).map(([k, v]) => (
                <p key={k} className="text-muted">
                  {k}: <span className="text-ink">{v ?? '—'}</span>
                </p>
              ))}
          </div>
        </Card>
      ))}
    </div>
  );
}
